import { hasTextAnswer, isMinigameType } from '@/components/challenges/challengeKinds';
import type { Challenge } from '@/config/objects/challenges';
import type { EventChallenge } from '@/config/objects/events';
import { DEFAULT_CHALLENGE_EDITOR_VALUES, type ChallengeEditorValues } from './types';
import { isAiValidatedAnswerType } from './validation';

/**
 * Mappers between API payloads and the unified `ChallengeEditorValues`.
 * All string↔number and null↔empty conversions live here — the rest of the
 * editor works with fully-typed values only.
 */

/** Trimmed string, or `null` when empty — the API's "clear this field". */
function trimOrNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Whether the current type+answerType combination carries text answers.
 * Puzzle/roadblock/minigame types never do (their `type` drives the whole
 * experience); AI-graded and puzzle answer types don't either.
 */
function carriesTextAnswers(values: ChallengeEditorValues): boolean {
  return (
    hasTextAnswer(values.type) &&
    !isAiValidatedAnswerType(values.answerType) &&
    values.answerType !== 'puzzle'
  );
}

/**
 * The answer to persist: only sent for answer-bearing kinds (true/false and
 * multiple-choice answers are stored in `answer` too — the selected option).
 * AI-validated, puzzle, roadblock, and minigame kinds persist `null`.
 */
function answerForPayload(values: ChallengeEditorValues): string | null {
  return carriesTextAnswers(values) ? trimOrNull(values.answer) : null;
}

/** Cleaned alternate answers (only meaningful for text-answer kinds). */
function alternateAnswersForPayload(values: ChallengeEditorValues): string[] {
  if (!carriesTextAnswers(values)) return [];
  return values.alternateAnswers.map(a => a.trim()).filter(a => a.length > 0);
}

/** Fields shared verbatim between the library and event payloads. */
function sharedPayload(values: ChallengeEditorValues): Record<string, unknown> {
  return {
    name: values.name.trim(),
    type: values.type,
    difficulty: values.difficulty,
    points: values.points,
    answerType: values.answerType,
    clue: values.clue.trim(),
    hint: trimOrNull(values.hint),
    hintCost: values.hintCost,
    answer: answerForPayload(values),
    alternateAnswers: alternateAnswersForPayload(values),
    choices: values.choices.filter(c => c.trim().length > 0),
    aiContext: trimOrNull(values.aiContext),
    zone: trimOrNull(values.zone),
    latitude: values.latitude,
    longitude: values.longitude,
    imageUrl: values.imageId || null,
    videoUrl: values.videoId || null,
    accessCode: trimOrNull(values.accessCode),
    // The prompt message only takes effect alongside a code.
    accessCodeMessage: trimOrNull(values.accessCode) ? trimOrNull(values.accessCodeMessage) : null,
    puzzleGridSize: values.puzzleGridSize,
    isTimed: values.isTimed,
    // Only include the config for minigame types — the API rejects a config
    // on a non-minigame challenge.
    ...(isMinigameType(values.type) ? { minigameConfig: values.minigameConfig } : {}),
  };
}

// ---------------------------------------------------------------------------
// Library
// ---------------------------------------------------------------------------

/**
 * Map a library challenge API entity to editor values.
 *
 * Accepts a partial so both editor contexts can share one loaded-entity type.
 *
 * @param c - Challenge returned by `GET /challenges/:id`.
 * @returns Fully-populated editor values.
 */
export function fromLibraryApi(c: Partial<Challenge>): ChallengeEditorValues {
  return {
    ...DEFAULT_CHALLENGE_EDITOR_VALUES,
    name: c.name ?? '',
    type: c.type ?? 'riddle',
    difficulty: c.difficulty ?? 'medium',
    points: c.points ?? 100,
    visibility: c.visibility ?? 'private',
    answerType: c.answerType ?? 'text',
    clue: c.clue ?? '',
    hint: c.hint ?? '',
    hintCost: c.hintCost ?? 0,
    answer: c.answer ?? '',
    alternateAnswers: c.alternateAnswers ?? [],
    choices: c.choices ?? [],
    aiContext: c.aiContext ?? '',
    minigameConfig: c.minigameConfig ?? null,
    puzzleGridSize: c.puzzleGridSize ?? DEFAULT_CHALLENGE_EDITOR_VALUES.puzzleGridSize,
    isTimed: c.isTimed ?? false,
    zone: c.zone ?? '',
    latitude: c.latitude ?? null,
    longitude: c.longitude ?? null,
    greaterAreaId: c.greaterAreaId ?? null,
    imageId: c.imageUrl ?? '',
    videoId: c.videoUrl ?? '',
    accessCode: c.accessCode ?? '',
    accessCodeMessage: c.accessCodeMessage ?? '',
    audioUrl: c.audioUrl ?? '',
  };
}

/**
 * Build the request body for `POST /challenges` / `PUT /challenges/:id`.
 *
 * @param values - Current editor values.
 * @returns API payload for the library challenge endpoints.
 */
export function toLibraryPayload(values: ChallengeEditorValues): Record<string, unknown> {
  return {
    ...sharedPayload(values),
    visibility: values.visibility,
    // Library-only: the managed greater area FK (null clears it).
    greaterAreaId: values.greaterAreaId,
  };
}

// ---------------------------------------------------------------------------
// Event
// ---------------------------------------------------------------------------

/**
 * Map an event challenge API entity to editor values.
 *
 * Accepts a partial so both editor contexts can share one loaded-entity type.
 *
 * @param c - Challenge returned by `GET /events/:id/challenges/:challengeId`.
 * @returns Fully-populated editor values.
 */
export function fromEventApi(c: Partial<EventChallenge>): ChallengeEditorValues {
  return {
    ...DEFAULT_CHALLENGE_EDITOR_VALUES,
    name: c.name ?? '',
    type: c.type ?? 'riddle',
    // Event difficulty may include 'expert' (legacy); clamp to the shared
    // scale so the select always has a valid value.
    difficulty: c.difficulty === 'expert' ? 'hard' : (c.difficulty ?? 'medium'),
    points: c.points ?? 100,
    answerType: c.answerType ?? 'text',
    clue: c.clue ?? '',
    hint: c.hint ?? '',
    hintCost: c.hintCost ?? 0,
    answer: c.answer ?? '',
    alternateAnswers: c.alternateAnswers ?? [],
    choices: c.choices ?? [],
    aiContext: c.aiContext ?? '',
    description: c.description ?? '',
    minigameConfig: c.minigameConfig ?? null,
    puzzleGridSize: c.puzzleGridSize ?? DEFAULT_CHALLENGE_EDITOR_VALUES.puzzleGridSize,
    isTimed: c.isTimed ?? false,
    zone: c.zone ?? '',
    latitude: c.latitude ?? null,
    longitude: c.longitude ?? null,
    mapIconColor: c.mapIconColor ?? '',
    mapIconImageId: c.mapIconUrl ?? '',
    imageId: c.imageUrl ?? '',
    videoId: c.videoUrl ?? '',
    accessCode: c.accessCode ?? '',
    accessCodeMessage: c.accessCodeMessage ?? '',
    maxCompletions: c.maxCompletions ?? null,
    maxAttempts: c.maxAttempts ?? null,
    timeLimitSeconds: c.timeLimitSeconds ?? null,
    attemptPenaltyPoints: c.attemptPenaltyPoints ?? null,
    attemptPenaltyPercent: c.attemptPenaltyPercent ?? null,
    speedBonuses: c.speedBonuses ?? [],
    requiresFacilitator: c.requiresFacilitator ?? false,
    facilitatorInstructions: c.facilitatorInstructions ?? '',
    activationMode: c.activationMode ?? 'available',
    activationRadius: c.activationRadius ?? null,
    ibeaconUuid: c.ibeaconUuid ?? '',
    ibeaconMajor: c.ibeaconMajor ?? null,
    ibeaconMinor: c.ibeaconMinor ?? null,
    audioUrl: c.audioUrl ?? '',
    qrCodeId: c.qrCodeId ?? '',
  };
}

/**
 * Build the request body for `PATCH /events/:id/challenges/:challengeId`.
 *
 * @param values - Current editor values.
 * @returns API payload for the event challenge update endpoint.
 */
export function toEventPayload(values: ChallengeEditorValues): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    ...sharedPayload(values),
    description: trimOrNull(values.description),
    maxCompletions: values.maxCompletions,
    maxAttempts: values.maxAttempts,
    timeLimitSeconds: values.timeLimitSeconds,
    attemptPenaltyPoints: values.attemptPenaltyPoints,
    attemptPenaltyPercent: values.attemptPenaltyPercent,
    speedBonuses: values.speedBonuses.filter(b => Number.isInteger(b) && b > 0),
    // Empty string clears the override; null falls back to the type default.
    mapIconColor: trimOrNull(values.mapIconColor),
    mapIconUrl: values.mapIconImageId || null,
    // Roadblock challenges implicitly require a facilitator (DT-164);
    // surface that as the boolean too so the API doesn't re-derive it.
    requiresFacilitator: values.requiresFacilitator || values.type === 'roadblock',
    facilitatorInstructions: trimOrNull(values.facilitatorInstructions),
    // Always send the activation mode so admins can flip back to `available`.
    activationMode: values.activationMode,
    activationRadius: values.activationMode === 'gps_proximity' ? values.activationRadius : null,
    // Null the beacon triple when the mode doesn't use it so a mode flip
    // never leaves stale beacon data behind.
    ibeaconUuid: values.activationMode === 'ibeacon' ? trimOrNull(values.ibeaconUuid) : null,
    ibeaconMajor: values.activationMode === 'ibeacon' ? values.ibeaconMajor : null,
    ibeaconMinor: values.activationMode === 'ibeacon' ? values.ibeaconMinor : null,
  };

  // The event schema's answerType enum has no 'puzzle' member — puzzle play
  // is keyed off `type`, so omit the field rather than fail validation.
  if (values.answerType === 'puzzle') {
    delete payload.answerType;
  }

  return payload;
}

/** Placement options for creating a challenge directly in an event. */
export interface EventCreateOptions {
  groupId?: string | null;
  sortOrder?: number;
  isStarter?: boolean;
  requireAll?: boolean;
}

/**
 * Build the request body for `POST /events/:id/challenges` when creating a
 * brand-new challenge inside an event (no library source). Wraps the full
 * field set under `challenge` per the extended create contract.
 *
 * @param values - Current editor values.
 * @param options - Optional placement (group, sort order, starter flags).
 * @returns API payload `{ challenge, groupId?, sortOrder?, isStarter?, requireAll? }`.
 */
export function toEventCreatePayload(
  values: ChallengeEditorValues,
  options: EventCreateOptions = {},
): Record<string, unknown> {
  return {
    challenge: toEventPayload(values),
    ...(options.groupId ? { groupId: options.groupId } : {}),
    ...(options.sortOrder !== undefined ? { sortOrder: options.sortOrder } : {}),
    ...(options.isStarter !== undefined ? { isStarter: options.isStarter } : {}),
    ...(options.requireAll !== undefined ? { requireAll: options.requireAll } : {}),
  };
}
